import dayjs from 'dayjs';
import hyLogo from 'assets/img/logos/hy-logo.png';

// Square (1080x1080) social-post image of a case study, drawn on a canvas so
// it needs no extra library and works entirely in the browser.
const SIZE = 1080;
const PAD = 64;
const GREEN = '#1f8a4c';
const DARK = '#1f2937';
const MUTED = '#6b7280';

const loadImage = src =>
  new Promise(resolve => {
    if (!src) return resolve(null);
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

// Word-wrap text into lines that fit maxWidth; returns the lines drawn count
const wrapLines = (ctx, text, maxWidth) => {
  const words = String(text || '')
    .split(/\s+/)
    .filter(Boolean);
  const lines = [];
  let line = '';
  words.forEach(word => {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  });
  if (line) lines.push(line);
  return lines;
};

const drawText = (ctx, text, x, y, maxWidth, lineHeight, maxLines) => {
  let lines = wrapLines(ctx, text, maxWidth);
  if (maxLines && lines.length > maxLines) {
    lines = lines.slice(0, maxLines);
    lines[maxLines - 1] = lines[maxLines - 1].replace(/\s*\S*$/, '') + '…';
  }
  lines.forEach((l, i) => ctx.fillText(l, x, y + i * lineHeight));
  return y + lines.length * lineHeight;
};

const drawCover = (ctx, img, x, y, w, h, radius) => {
  const scale = Math.max(w / img.width, h / img.height);
  const sw = w / scale;
  const sh = h / scale;
  const sx = (img.width - sw) / 2;
  const sy = (img.height - sh) / 2;
  ctx.save();
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, radius);
  ctx.clip();
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
  ctx.restore();
};

export async function renderCaseStudyImage(caseStudy) {
  const { orphan, title, quote, study_date, photos } = caseStudy;
  const childName = orphan
    ? [orphan.first_name, orphan.middle_name, orphan.last_name]
        .filter(Boolean)
        .join(' ')
    : '';
  const heroSrc =
    orphan?.image ||
    (Array.isArray(photos) && photos.length ? photos[0] : null);
  const [logo, hero] = await Promise.all([
    loadImage(hyLogo),
    loadImage(heroSrc)
  ]);

  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');

  // Background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, SIZE, SIZE);
  // Green header band
  ctx.fillStyle = GREEN;
  ctx.fillRect(0, 0, SIZE, 150);
  if (logo) {
    const h = 70;
    const w = (logo.width / logo.height) * h;
    // white plate so the logo reads on green
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.roundRect(PAD - 16, 40, w + 32, h + 24, 12);
    ctx.fill();
    ctx.drawImage(logo, PAD, 52, w, h);
  }
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 30px Helvetica, Arial, sans-serif';
  ctx.textAlign = 'right';
  ctx.fillText('CASE STUDY', SIZE - PAD, 95);
  ctx.textAlign = 'left';

  // Hero photo + title/quote block
  let y = 150 + PAD;
  const photoSize = 340;
  if (hero) {
    drawCover(ctx, hero, PAD, y, photoSize, photoSize, 24);
  } else {
    ctx.fillStyle = '#f3f4f6';
    ctx.beginPath();
    ctx.roundRect(PAD, y, photoSize, photoSize, 24);
    ctx.fill();
  }
  const textX = PAD + photoSize + 40;
  const textW = SIZE - PAD - textX;

  ctx.fillStyle = DARK;
  ctx.font = 'bold 44px Helvetica, Arial, sans-serif';
  let ty = drawText(ctx, title, textX, y + 44, textW, 52, 3);

  ctx.fillStyle = MUTED;
  ctx.font = '26px Helvetica, Arial, sans-serif';
  const meta = [
    childName,
    orphan?.country,
    study_date ? dayjs(study_date).format('MMMM YYYY') : null
  ]
    .filter(Boolean)
    .join(' · ');
  ty = drawText(ctx, meta, textX, ty + 12, textW, 34, 2);

  if (quote) {
    ctx.fillStyle = GREEN;
    ctx.fillRect(textX, ty + 14, 6, Math.min(170, photoSize - (ty - y) - 10));
    ctx.fillStyle = DARK;
    ctx.font = 'italic 30px Helvetica, Arial, sans-serif';
    drawText(ctx, `“${quote}”`, textX + 24, ty + 48, textW - 24, 40, 4);
  }

  // Sections
  y += photoSize + 48;
  const sections = [
    ['Before Help Yateem', caseStudy.life_before],
    ['How they found us', caseStudy.how_found_out],
    ['Their experience', caseStudy.experience],
    ['How they are now', caseStudy.life_now]
  ].filter(([, t]) => t);
  const bottomLimit = SIZE - 110;
  const perSection = sections.length
    ? Math.floor((bottomLimit - y) / sections.length)
    : 0;
  const bodyLines = Math.max(2, Math.floor((perSection - 44) / 30));
  sections.forEach(([label, text]) => {
    if (y + 44 > bottomLimit) return;
    ctx.fillStyle = GREEN;
    ctx.font = 'bold 26px Helvetica, Arial, sans-serif';
    ctx.fillText(label.toUpperCase(), PAD, y + 26);
    ctx.fillStyle = '#374151';
    ctx.font = '24px Helvetica, Arial, sans-serif';
    drawText(ctx, text, PAD, y + 60, SIZE - PAD * 2, 30, bodyLines);
    y += perSection;
  });

  // Footer
  ctx.fillStyle = '#e5e7eb';
  ctx.fillRect(PAD, SIZE - 80, SIZE - PAD * 2, 2);
  ctx.fillStyle = MUTED;
  ctx.font = '22px Helvetica, Arial, sans-serif';
  ctx.fillText(
    'Help Yateem · Changing lives through sponsorship',
    PAD,
    SIZE - 40
  );
  ctx.textAlign = 'right';
  ctx.fillText('helpyateem.org', SIZE - PAD, SIZE - 40);
  ctx.textAlign = 'left';

  return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
}

export async function downloadCaseStudyImage(caseStudy) {
  const blob = await renderCaseStudyImage(caseStudy);
  if (!blob) throw new Error('Could not render image');
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const name = caseStudy.orphan
    ? `${caseStudy.orphan.first_name}-${caseStudy.orphan.last_name}`
    : 'beneficiary';
  a.href = url;
  a.download = `case-study-${name}-${caseStudy.study_date || ''}.png`
    .replace(/\s+/g, '-')
    .toLowerCase();
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
